// MA-471 — NARRATION UI STRINGS: the single source of truth for STATIC
// kid-facing narration literals spoken by useNarration()'s `speak()` at
// call sites OUTSIDE SpeakPromptButton/ReaderNarrationButton (which speak
// content-pack/story text — already the whole reason
// scripts/content/narratableStrings.mjs's practice/library enumerators
// exist).
//
// THE DEFECT THIS FILE CLOSES (MA-471): IDontKnowChip, ChunkCheckpoint,
// FavoriteHeartButton, and StoryLibraryScreen each `speak()` a small, fixed
// set of kid-facing UI copy — "Not sure? Tap I don't know and I'll help.",
// the four ChunkCheckpoint tutor lines, the two FavoriteHeartButton
// confirmations, "Practice reading to unlock this story!" — that
// narratableStrings.mjs never enumerated. Every one of those strings was
// therefore OUTSIDE the practice denominator narrationCoverageGate.mjs
// grades: a real session could hear a page's narration in the
// pre-generated "sage" voice, hit one of these UI moments and flip to the
// flat browser robot voice, then flip BACK to "sage" on the next page —
// invisible to a coverage report reading 100% PASS, audible to every kid.
//
// ⭐SUPERSEDED BY MA-476 (founder ruling 2026-08-03, "it needs to be
// everywhere"): this file used to say the interpolated call sites were
// "DELIBERATELY excluded ... by design". They are not, any more. ReadTab's
// reread invitation and books-read pebble are TEMPLATED HELPERS below whose
// finite domains narratableStrings.mjs expands; ExitConfirmDialog's spoken copy
// was re-authored to drop the child's name. The one thing still true is the
// REASON: a genuinely unbounded domain (a child's name) cannot be
// pre-generated, so the fix is to change the COPY, never to add an exemption.
//
// Each component imports its own strings FROM here (never the reverse) —
// the same anti-drift discipline that produced narrationConstants.ts: a
// re-typed copy inside narratableStrings.mjs's enumerator could silently
// drift from what a component actually speaks, which would make the gate
// "mutually consistent but wrong" with the runtime, exactly the failure
// mode narrationCoverageGate.mjs's own header warns about for the shared
// pack-string enumerator.
//
// These call sites pass RAW text straight to `speak()` — none of them run
// their string through `speakableText()` the way SpeakPromptButton's
// prompts do (verified against each call site below before this file was
// written). scripts/content/narratableStrings.mjs's `enumerateUiStrings()`
// therefore keys its denominator entries on
// `normalizeNarrationText(raw)`, NOT `normalizeNarrationText(speakableText(raw))`
// — using `speakableText` here would generate/check audio for a string the
// runtime never actually requests.

/** ⭐MA-482 — the chip used to speak ONE line, "Not sure? Tap I don't know and
 *  I'll help.", on every screen. On three of the four, tapping it does not help:
 *  it reveals the answer and advances (`ReadingSession.tsx:1040`,
 *  `MixedSession.tsx:828`, `FoundationSession.tsx:738`), and for a trace item it
 *  advances showing nothing at all. The founder reported it on 2026-08-04.
 *
 *  A §3b red panel killed the proposed fix (a two-stage hint ladder) — see
 *  MA-482's decision log for the content counts that killed it — and what
 *  survived is this: say the true thing per surface. The visible label stays
 *  "I don't know"; only the SPOKEN promise changes.
 *
 *  ⚠️A closed key set and a fixed Record, deliberately — the
 *  CHUNK_CHECKPOINT_TUTOR_LINES shape below. Finite keys, finite strings, ZERO
 *  interpolation, every value in NARRATION_UI_STRINGS. A per-item promise
 *  ("we'll sound out CAT") would be an unbounded domain that
 *  narratableStrings.mjs cannot expand, so it would ship as the robot voice at
 *  the exact moment a child asked for help — the MA-476/477 defect class. */
export type IDontKnowPromise = "reveal" | "coach" | "moveOn" | "clue";

export const IDK_CHIP_PROMISE_TEXT: Record<IDontKnowPromise, string> = {
  /** The item has a right answer to show, and showing it is all that happens. */
  reveal: "Not sure? Tap I don't know and I'll show you the answer.",
  /** Math addition with an apple worked example — the one genuine scaffold. */
  coach: "Not sure? Tap I don't know and we'll count them together.",
  /** Nothing to reveal: a trace letter, or any item with no answer surface. */
  moveOn: "Not sure? Tap I don't know and we'll try the next one.",
  /** ⚠️Dormant since MA-489. Was passed by the two-stage rung (MA-483/486:
   *  first press shows a clue, second press reveals) on the items with a
   *  scaffold to show; that rung was REMOVED per founder ruling ("i don't
   *  know gives an answer, not a hint uncovering"). Kept generated (not
   *  deleted) in case a future scaffold pays out help after a decline and
   *  needs this line again — deleting it now would cost a second audio
   *  batch to bring back. NOT passed by any screen today. */
  clue: "Not sure? Tap I don't know and we'll look at a clue first.",
};

/** ChunkCheckpoint.tsx's per-strand tutor line — one calm, story-led line
 *  spoken (best-effort autoplay, with a guaranteed tap-to-replay fallback)
 *  when a block of questions completes. Four fixed strands, four fixed
 *  strings — never interpolated. */
export const CHUNK_CHECKPOINT_TUTOR_LINES: Record<"math" | "reading" | "mixed" | "foundation", string> = {
  math: "Nice counting! Shake out your hands, then we’ll do five more.",
  reading: "Lovely reading. Take a breath — the next words are waiting.",
  mixed: "You’re mixing it up brilliantly. Ready for the next five?",
  foundation: "Beautiful letters. Let’s keep our pencil moving.",
};

/** FavoriteHeartButton.tsx's spoken confirmation — one of exactly these two
 *  fixed strings, never the book title (the toggle direction alone decides
 *  which). */
export const FAVORITE_ADDED_SPEAK_TEXT = "Added to your favorites!";
export const FAVORITE_REMOVED_SPEAK_TEXT = "Removed from favorites";

/** StoryLibraryScreen.tsx's locked-tile tap affordance — a pre-reader who
 *  taps a locked story hears why, instead of nothing happening. */
export const STORY_LOCKED_SPEAK_TEXT = "Practice reading to unlock this story!";

/** ⭐MA-507 slice 2 — StoryReader.tsx's reader-chrome speaker controls.
 *  Live-caught (2026-08-13 §3d journey/reward walkthrough, artifact
 *  `docs/build/reports/journey-v3-reward-live-verify-2026-08-13.md`): the
 *  header's "Back to library" SpeakPromptButton spoke via `speechSynthesis`
 *  because this kid-audible string was never enumerated into
 *  `NARRATION_UI_STRINGS` — the MA-471/476 registration class, the same
 *  shape MA-500 closed for Journey. A sweep of the rest of the reader-chrome
 *  surface (every `speak()`/`playDecodeSupport()` call site reachable from
 *  StoryReader.tsx and its siblings — SpeakPromptButton, ReaderNarrationButton,
 *  FavoriteHeartButton, the tap-a-word decode path) found exactly one sibling
 *  in the same unregistered state: the one-time "Tap a word to hear it read!"
 *  self-describe hint (MA-281), also spoken via a SpeakPromptButton, also a
 *  raw inline literal never folded into this file. Both are now the single
 *  source StoryReader.tsx imports FROM (never a re-typed literal) — the same
 *  parity-by-construction discipline every other export in this file
 *  follows. Both strings pass through SpeakPromptButton's `speakableText()`
 *  at runtime same as any other prompt, but neither contains any
 *  cloze/operator/phoneme notation `speakableText` rewrites, so
 *  `speakableText(raw) === raw` for both — safe to enumerate here as a raw
 *  literal alongside this file's other `NARRATION_UI_STRINGS`, whose own
 *  header documents why raw (not `speakableText`-passed) is the correct key
 *  for THIS file's call sites. */
export const READER_BACK_TO_LIBRARY_SPEAK_TEXT = "Back to library";
export const READER_TAP_A_WORD_HINT_SPEAK_TEXT = "Tap a word to hear it read!";

/** ⭐MA-516 — a founder asked, while looking at the "Who's playing?" child
 *  picker, "is there a way to know where else we have robotic voice" (the
 *  same question that produced MA-509/510's runtime telemetry). A sweep of
 *  every SpeakPromptButton/ReaderNarrationButton call site against the real
 *  enumerator (the same cross-reference MA-507 slice 2 did for StoryReader)
 *  found two more unregistered call sites, both in StoryLibraryScreen.tsx:
 *  the "Back to your shelf" button, and the ten SUBJECT_LABELS shelf-row
 *  headers (src/content/subjectLabels.ts). `speakableText(raw) === raw` was
 *  verified for this literal and for every one of the ten SUBJECT_LABELS
 *  values (none contain cloze/operator/phoneme notation) — safe to
 *  enumerate as raw literals, same as READER_BACK_TO_LIBRARY_SPEAK_TEXT
 *  above. SUBJECT_LABELS itself is NOT imported here — it needs `import
 *  type { SubjectSlug } from "./stories"`, which would violate this file's
 *  own zero-import rule (see header) — so scripts/content/narratableStrings.mjs's
 *  enumerator imports it directly instead, the same split
 *  journeyStoneSpeak.ts/journeyPickSpeak.ts already established. */
export const BACK_TO_SHELF_SPEAK_TEXT = "Back to your shelf";

/** ExitConfirmDialog.tsx's spoken leave-the-session copy. ⭐The DISPLAYED
 *  message keeps the child's first name ("Mia's answers so far are saved");
 *  this SPOKEN twin deliberately does not. A child's name is the one
 *  interpolation no build step can enumerate, so a name-bearing utterance
 *  could never be a pre-generated asset — it would fall back to the browser
 *  voice for every child forever (MA-476). */
export const EXIT_CONFIRM_SPEAK_TEXT =
  "Leave the session? Your answers so far are saved. Finishing the session is what adds today's story page.";

/** ⭐MA-496 (Journey v3, PR-A / A10 + A12) — the ribbon path's stone-tap
 *  SPEAK text. PR-A's tap posture is narrate-only (no launch, no pick):
 *  "taps SPEAK the stone's kid label + status phrase" — honest at every
 *  instant. Composed from TWO already-closed domains (journeyView.ts's
 *  `KID_LABEL_MAP`: 54 values, `KID_STATUS_LABEL`: 7 values) rather than
 *  re-typing either here — every call site MUST go through this function
 *  (never build the string inline), the same MA-477-style discipline this
 *  file's other closed-domain lookups follow.
 *
 *  The full 54*7 = 378-string domain (JOURNEY_STONE_SPEAK_STRINGS) is
 *  assembled in src/lib/engine/journeyStoneSpeak.ts, which imports both
 *  Records — kept OUT of this import-free file on purpose (every other
 *  export here is a bare literal/function with zero imports; see this
 *  file's header). Live call site verified: JourneyPath.tsx's `handleTap`
 *  calls `speak(speakTextForStone(stone.kidLabel, stone.status))` for
 *  EVERY non-pick-eligible tap, unconditionally — this is the domain that
 *  actually fires every time a child taps a stone that doesn't launch or
 *  pick, which is most stones most of the time. ⭐MA-500 folds this
 *  378-string domain into scripts/content/narratableStrings.mjs's
 *  enumerator (the second step of the two-step pattern MA-496 started);
 *  pre-baked audio for it was generated by `narration-regenerate.yml` run
 *  31667356805 (the temporary PENDING_NARRATION_MA500_JOURNEY_ARC grading
 *  carve-out that bridged the gap has since been removed). */
export function stoneTapSpeakText(kidLabel: string, statusPhrase: string): string {
  return `${kidLabel} — ${statusPhrase}.`;
}

/** ⭐MA-496 (§4 + A8 + A12) — the pal's speech-BUBBLE text: ON-SCREEN
 *  display copy for the cartoon speech bubble rendered above the pal
 *  marker, never TTS narration and never passed to `speak()`/
 *  `useNarration`. The DISPLAYED bubble keeps the child's name for two of
 *  four variants (`turn`/`go`, built inline in JourneyPath.tsx's
 *  `PalMarker` as a template literal — never passed to this Record); the
 *  other two (`ready`/`conquest`) are already name-free and pulled from
 *  this Record for their on-screen text.
 *
 *  ⭐MA-500 FINDING / MA-504 RESOLUTION: this const was previously named
 *  `PAL_BUBBLE_SPOKEN_TEXT` and its doc comment claimed it was "the
 *  finite, name-free domain actually passed to speak()". MA-500 verified
 *  by exhaustive grep (`grep -rn "PAL_BUBBLE_SPOKEN_TEXT" src`) that this
 *  was false: the only call sites are the two DISPLAY-text usages in
 *  JourneyPath.tsx's `PalMarker`, and nothing anywhere passes this
 *  Record's values to `speak()`. MA-504 (operator product call, 2026-08-13)
 *  renamed it to `PAL_BUBBLE_TEXT` to stop the name claiming a narration
 *  role it never had — no behavior, no rendering, and no spoken string
 *  changed; this is purely a rename to make the name honest. See MA-504's
 *  BACKLOG decision log. */
export const PAL_BUBBLE_TEXT = {
  turn: "Your turn!",
  go: "Let's go!",
  ready: "This one's ready for you!",
  conquest: "You did it! Look how far you've come!",
} as const;

export type PalBubbleVariant = keyof typeof PAL_BUBBLE_TEXT;

/** The DAY-ROTATED variant order (A12) — deterministic, never random:
 *  `dayIndex % PAL_BUBBLE_DAY_ROTATION.length` picks one of the three
 *  "still waiting" variants (the post-conquest variant is selected
 *  separately, only on a fresh burst, never by rotation). */
export const PAL_BUBBLE_DAY_ROTATION: readonly PalBubbleVariant[] = ["turn", "go", "ready"];

/** ⭐MA-497 (Journey v3, PR-B / A12) — MA-DEC-0812's day-pick tap feedback.
 *  Three fixed spoken moments per §5: ADD speaks the stone's kid label +
 *  "Added to your path!" (a TEMPLATED, finite-domain utterance — the
 *  function below, same MA-476 discipline as `stoneTapSpeakText`); REMOVE
 *  and the cap-tap both speak one fixed, name-free line each. The 54-entry
 *  ADD domain (`JOURNEY_PICK_ADDED_STRINGS`) is assembled in
 *  src/lib/engine/journeyPickSpeak.ts (imports `journeyView.ts`'s
 *  KID_LABEL_MAP — kept OUT of this import-free file, mirroring
 *  journeyStoneSpeak.ts's own split from JOURNEY_STONE_SPEAK_STRINGS).
 *
 *  Live call sites verified in JourneyPath.tsx's `handleTap`: ADD ->
 *  `speak(speakTextForPickAdded(stone.kidLabel))`; REMOVE ->
 *  `speak(JOURNEY_PICK_REMOVED_SPEAK_TEXT)`; cap-tap ->
 *  `speak(JOURNEY_PICK_CAP_SPEAK_TEXT)` — all three genuinely reachable,
 *  unlike the pal-bubble display text above, which is never passed to
 *  speak() (see its own MA-500/MA-504 doc comment). ⭐MA-500 folds JOURNEY_PICK_REMOVED_SPEAK_TEXT
 *  and JOURNEY_PICK_CAP_SPEAK_TEXT into NARRATION_UI_STRINGS below (they are
 *  bare literals, the same shape as FAVORITE_ADDED_SPEAK_TEXT), and folds the
 *  54-entry JOURNEY_PICK_ADDED_STRINGS domain into
 *  scripts/content/narratableStrings.mjs's enumerator directly (same
 *  import-from-journeyPickSpeak.ts shape as JOURNEY_STONE_SPEAK_STRINGS) —
 *  pre-baked audio for it was generated by `narration-regenerate.yml` run
 *  31667356805 (the temporary PENDING_NARRATION_MA500_JOURNEY_ARC grading
 *  carve-out that bridged the gap has since been removed). */
export function journeyPickAddedSpeakText(kidLabel: string): string {
  return `${kidLabel} — added to your path!`;
}

/** MA-DEC-0812 §5 — "Remove: tap a picked stone again -> flag retracts,
 *  pal: 'Okay, maybe later!'" — one fixed, name-free line. */
export const JOURNEY_PICK_REMOVED_SPEAK_TEXT = "Okay, maybe later!";

/** MA-DEC-0812 §5 — "Cap 2 concurrent picks -- tapping a third narrates
 *  'Two picks already — finish one first!'" — one fixed, name-free line. */
export const JOURNEY_PICK_CAP_SPEAK_TEXT = "Two picks already — finish one first!";

/** ⭐MA-476 — the three TEMPLATED kid-facing utterances. Each is a function,
 *  not a bare literal, and each has a FINITE domain that
 *  scripts/content/narratableStrings.mjs expands into the coverage
 *  denominator, so every variant is a real generated asset rather than a
 *  silent WebSpeech fallback.
 *
 *  ⚠️Call sites must pass the FUNCTION RESULT to speak(), never an inline
 *  template literal — the MA-477 lint fails a speak() argument that is a
 *  template literal containing an expression, and these helpers are what
 *  makes satisfying that lint possible instead of merely annoying. */

/** CoachHint.tsx counts apples aloud as the child taps them. The domain is
 *  1..COUNT_ALOUD_MAX; MathSession only routes here for positive addition, and
 *  the coach's worked example is small by construction. */
export const COUNT_ALOUD_MAX = 20;
export function countAloudText(n: number): string {
  return String(n);
}

/** ⭐MA-1068 (D-1026-34/35) — CoachHint.tsx speaks ONE count-on line when the
 *  apple coach opens on an ms.math.09 item (the item's OWN skill, never the
 *  session's focal skill): count on from the FIRST addend, listing every
 *  number — "Say 5. Count on: 6, 7." That is what the ms.math.09 lesson
 *  teaches (the larger-addend strategy is a later skill), and the operator
 *  ruled the full list is spoken however long b is (max today b = 9, 1 + 9).
 *  Other skills' coach speaks no line on open (unchanged). */
export const COUNT_ON_COACH_SKILL_ID = "ms.math.09";
export function countOnCoachText(a: number, b: number): string {
  const counts = Array.from({ length: b }, (_, i) => String(a + i + 1));
  return `Say ${a}. Count on: ${counts.join(", ")}.`;
}
/** The (a, b) domain: every distinct operand pair the ms.math.09 served pools
 *  (both audiences, through the real mapper) route to the apple worked example
 *  (hasAppleCoachShape and coachStatesAnswer). This file cannot import the
 *  pools (zero-import rule), so the list is literal; ma1068CountOnCoach.test
 *  fails when it is not EXACTLY the served set (a new ms.math.09 pair would
 *  otherwise speak an unrecorded line). 44 pairs, 2026-10-26. */
export const COUNT_ON_COACH_PAIRS: readonly (readonly [number, number])[] = [
  [1, 1], [1, 2], [1, 3], [1, 4], [1, 5], [1, 6], [1, 7], [1, 8], [1, 9],
  [2, 1], [2, 2], [2, 3], [2, 4], [2, 5], [2, 6], [2, 7], [2, 8],
  [3, 1], [3, 3], [3, 4], [3, 5], [3, 6], [3, 7],
  [4, 1], [4, 2], [4, 3], [4, 4], [4, 5], [4, 6],
  [5, 1], [5, 2], [5, 3], [5, 4], [5, 5],
  [6, 1], [6, 2], [6, 3], [6, 4],
  [7, 1], [7, 2], [7, 3],
  [8, 1], [8, 2],
  [9, 1],
];
/** The 44 spoken count-on lines, one per pair above. */
export const COUNT_ON_COACH_STRINGS: readonly string[] = COUNT_ON_COACH_PAIRS.map(([a, b]) => countOnCoachText(a, b));

/** ReadTab.tsx's reread invitation. Domain = every readable story title, which
 *  the enumerator reads from the same story index the screen does. */
export function rereadPromptText(title: string): string {
  return `Want to read ${title} again?`;
}

/** ReadTab.tsx's books-read pebble. Domain = 1..BOOKS_READ_SPOKEN_MAX, with a
 *  single count-free phrasing above the cap so the domain stays finite however
 *  many books a child eventually reads. */
export const BOOKS_READ_SPOKEN_MAX = 50;
export const BOOKS_READ_MANY_TEXT = "You've read lots of books!";
export function booksReadText(n: number): string {
  if (n > BOOKS_READ_SPOKEN_MAX) return BOOKS_READ_MANY_TEXT;
  return `You've read ${n} ${n === 1 ? "book" : "books"}!`;
}

/** Every static kid-facing narration string this file owns, flattened —
 *  the ONE array both this file's own tests and
 *  scripts/content/narratableStrings.mjs's `enumerateUiStrings()` iterate,
 *  so a string added above is automatically in the coverage-gate
 *  denominator with no second place that has to remember to list it. */
export const NARRATION_UI_STRINGS: readonly string[] = [
  ...Object.values(IDK_CHIP_PROMISE_TEXT),
  ...Object.values(CHUNK_CHECKPOINT_TUTOR_LINES),
  FAVORITE_ADDED_SPEAK_TEXT,
  FAVORITE_REMOVED_SPEAK_TEXT,
  STORY_LOCKED_SPEAK_TEXT,
  // ⭐MA-507 slice 2 — StoryReader.tsx's reader-chrome sweep (see the two
  // consts' own doc comment above): "Back to library" and the tap-a-word
  // self-describe hint, both spoken via SpeakPromptButton and both
  // previously raw inline literals never enumerated here.
  READER_BACK_TO_LIBRARY_SPEAK_TEXT,
  READER_TAP_A_WORD_HINT_SPEAK_TEXT,
  // ⭐MA-516 — StoryLibraryScreen.tsx's "Back to your shelf" button (see the
  // const's own doc comment above). The ten SUBJECT_LABELS values are NOT
  // added here — they cannot be, per this file's zero-import rule — they
  // are folded into the denominator directly by
  // scripts/content/narratableStrings.mjs's enumerator instead.
  BACK_TO_SHELF_SPEAK_TEXT,
  EXIT_CONFIRM_SPEAK_TEXT,
  // ⭐MA-500 — MA-497's two fixed, name-free pick-feedback lines (the third,
  // JOURNEY_PICK_ADDED_STRINGS, is a 54-entry templated domain folded in by
  // scripts/content/narratableStrings.mjs directly, not here — see that
  // const's own doc comment above).
  JOURNEY_PICK_REMOVED_SPEAK_TEXT,
  JOURNEY_PICK_CAP_SPEAK_TEXT,
  // PAL_BUBBLE_TEXT (renamed from PAL_BUBBLE_SPOKEN_TEXT by MA-504) is
  // intentionally NOT folded in here — it is display-only, never passed to
  // speak() (see its own doc comment above). The MA-500-era
  // JOURNEY_TAP_NARRATE_TEXT dead-code domain that used to be named here
  // alongside it was deleted by MA-504 (see MA-504's BACKLOG decision log).
];

/** ⭐MA-476 — the expanded variants of the TEMPLATED utterances above, for the
 *  two templates whose domain this file can enumerate on its own (counting
 *  numbers and the books-read pebble). The reread invitation is NOT here: its
 *  domain is the story index, which is content the enumerator owns, so
 *  narratableStrings.mjs expands that one. Keeping the split explicit stops a
 *  future reader from "tidying" the story-index read into this engine file. */
export const NARRATION_TEMPLATED_STRINGS: readonly string[] = [
  ...Array.from({ length: COUNT_ALOUD_MAX }, (_, i) => countAloudText(i + 1)),
  ...Array.from({ length: BOOKS_READ_SPOKEN_MAX }, (_, i) => booksReadText(i + 1)),
  BOOKS_READ_MANY_TEXT,
  // ⭐MA-1068 — the 44 ms.math.09 count-on coach lines (see the const above).
  ...COUNT_ON_COACH_STRINGS,
];
